'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { useLocale } from '@/contexts/LocaleContext';
import { PAYOUT_METHODS, formatCurrency, COUNTRY_LIST } from '@/lib/geoLocale';
import { getRegionFromCountry } from '@/lib/geoLocale';

interface Payout {
  id: string;
  amount_inr: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  notes: string;
  requested_at: string;
  processed_at: string | null;
}

interface Balances {
  available: number;
  pendingClearance: number;
  lifetimePaid: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  processing: { label: 'Processing', color: '#818cf8', bg: 'rgba(99,102,241,0.12)' },
  completed: { label: 'Completed', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

export default function PayoutsPage() {
  const { currencyConfig, selectedCountry, setCountry } = useLocale();
  const [balances, setBalances] = useState<Balances | null>(null);
  const [history, setHistory] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNotes, setWithdrawNotes] = useState('');
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [bankProfile, setBankProfile] = useState<{
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    ifscCode: string;
    upiId: string;
    verified: boolean;
  } | null>(null);
  const [bankLoading, setBankLoading] = useState(true);

  const region = getRegionFromCountry(selectedCountry);
  const payoutMethods = PAYOUT_METHODS[region] || PAYOUT_METHODS['ROW'];
  const isIndia = selectedCountry === 'IN';

  // Convert INR balance to local currency (approximate)
  function convertBalance(inrAmount: number): string {
    const usdAmount = inrAmount * 0.012;
    const localAmount = usdAmount / currencyConfig.usdEquivalent;
    return formatCurrency(localAmount, currencyConfig);
  }

  function toUSDFromInr(inrAmount: number): string {
    const usd = inrAmount * 0.012;
    return `$${usd.toFixed(2)}`;
  }

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/payouts');
      if (!res.ok) throw new Error('Failed to load payout data');
      const json = await res.json();
      setBalances({ available: json.available || 0, pendingClearance: json.pendingClearance || 0, lifetimePaid: json.lifetimePaid || 0 });
      setHistory(json.history || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchBankProfile() {
    setBankLoading(true);
    try {
      const res = await fetch('/api/user/bank-details');
      if (!res.ok) return;
      const json = await res.json();
      const d = json.bankDetails;
      if (d && (d.account_holder_name || d.account_number)) {
        setBankProfile({
          accountHolderName: d.account_holder_name || '',
          bankName: d.bank_name || '',
          accountNumber: d.account_number ? `••••${d.account_number.slice(-4)}` : '',
          ifscCode: d.ifsc_code || '',
          upiId: d.upi_id || '',
          verified: !!(d.account_holder_name && d.account_number),
        });
      }
    } catch (e) {
      // silently fail
    } finally {
      setBankLoading(false);
    }
  }

  useEffect(() => { fetchData(); fetchBankProfile(); }, []);
  useEffect(() => {
    if (payoutMethods.methods.length > 0) {
      setSelectedPayoutMethod(payoutMethods.methods[0]);
    }
  }, [selectedCountry]);

  async function handleWithdraw(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const amount = Number(withdrawAmount);
    const minAmount = isIndia ? 2500 : 10;
    if (amount < minAmount) {
      setSubmitError(`Minimum withdrawal amount is ${isIndia ? '₹2,500' : `${currencyConfig.symbol}10`}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/payouts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, notes: withdrawNotes, payoutMethod: selectedPayoutMethod }),
      });
      const json = await res.json();
      if (!res.ok) { setSubmitError(json.error || 'Request failed'); return; }
      setSubmitSuccess(true);
      setShowModal(false);
      setWithdrawAmount('');
      setWithdrawNotes('');
      fetchData();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (e: any) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Payouts</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Manage your earnings withdrawals</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Icon name="GlobeAltIcon" size={16} style={{ color: 'var(--muted-foreground)' }} />
              <select
                value={selectedCountry}
                onChange={(e) => setCountry(e.target.value)}
                className="text-xs px-2 py-1.5 rounded-lg outline-none"
                style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
              >
                {COUNTRY_LIST.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white"
              style={{ background: 'var(--primary)' }}
            >
              <Icon name="PlusIcon" size={16} />
              New Request
            </button>
          </div>
        </div>

        {submitSuccess && (
          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
            <Icon name="CheckCircleIcon" size={18} style={{ color: '#10b981' }} />
            <p className="text-sm font-semibold" style={{ color: '#10b981' }}>Withdrawal request submitted successfully!</p>
          </div>
        )}

        {/* Balance Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: 'Available Balance', inr: balances?.available || 0, icon: 'BanknotesIcon', color: 'var(--success)', bg: 'var(--success-bg)', note: 'Ready to withdraw' },
                { label: 'Pending Clearance', inr: balances?.pendingClearance || 0, icon: 'ClockIcon', color: 'var(--warning)', bg: 'var(--warning-bg)', note: 'Processing' },
                { label: 'Lifetime Paid', inr: balances?.lifetimePaid || 0, icon: 'CheckCircleIcon', color: 'var(--primary-light)', bg: 'var(--purple-bg)', note: 'Total paid out' },
              ].map((card) => (
                <div key={card.label} className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: card.bg }}>
                      <Icon name={card.icon as any} size={20} style={{ color: card.color }} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold font-mono-data" style={{ color: 'var(--foreground)' }}>
                    {isIndia ? `₹${card.inr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : convertBalance(card.inr)}
                  </p>
                  {!isIndia && (
                    <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      ≈ {toUSDFromInr(card.inr)} USD
                    </p>
                  )}
                  <p className="text-xs font-medium mt-1" style={{ color: 'var(--muted-foreground)' }}>{card.label}</p>
                  <p className="text-xs mt-0.5" style={{ color: card.color }}>{card.note}</p>
                </div>
              ))}
            </div>

            {/* Connected Payment Channel — auto-populated from Legal Documents */}
            <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon name="CreditCardIcon" size={18} style={{ color: 'var(--primary-light)' }} />
                  <h3 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                    Connected Payment Channel
                  </h3>
                </div>
                {bankProfile?.verified && (
                  <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
                    <Icon name="CheckCircleIcon" size={13} />
                    Verified &amp; Active
                  </span>
                )}
              </div>

              {bankLoading ? (
                <div className="flex items-center gap-3 py-4">
                  <div className="w-5 h-5 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Loading payment details...</span>
                </div>
              ) : bankProfile?.verified ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: 'Account Holder', value: bankProfile.accountHolderName },
                    { label: 'Bank Name', value: bankProfile.bankName },
                    { label: 'Account Number', value: bankProfile.accountNumber },
                    { label: isIndia ? 'IFSC Code' : 'SWIFT / Routing', value: bankProfile.ifscCode },
                    ...(bankProfile.upiId ? [{ label: isIndia ? 'UPI ID' : 'PayPal Email', value: bankProfile.upiId }] : []),
                  ].map((row) => (
                    <div key={row.label} className="p-3 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                      <p className="text-2xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--muted-foreground)' }}>{row.label}</p>
                      <p className="text-sm font-medium font-mono-data" style={{ color: 'var(--foreground)' }}>{row.value || '—'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <Icon name="ExclamationTriangleIcon" size={20} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>No payment details on file</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      Add your bank details in{' '}
                      <a href="/legal-documents" className="underline font-semibold" style={{ color: 'var(--primary-light)' }}>
                        Legal &amp; Compliance
                      </a>{' '}
                      to enable withdrawals. Your details will automatically appear here.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Minimum threshold notice */}
            <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
              <Icon name="InformationCircleIcon" size={18} style={{ color: '#818cf8' }} />
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Minimum withdrawal threshold is{' '}
                <span className="font-bold" style={{ color: 'var(--foreground)' }}>
                  {isIndia ? '₹2,500' : `${currencyConfig.symbol}10`}
                </span>.{' '}
                {isIndia ? 'Verified Indian bank details required.' : 'Verified payout account required.'}
              </p>
            </div>

            {/* Withdrawal History */}
            <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <h3 className="text-base font-bold mb-5" style={{ color: 'var(--foreground)' }}>Withdrawal History</h3>
              {history.length === 0 ? (
                <div className="text-center py-10">
                  <Icon name="BanknotesIcon" size={32} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No withdrawal requests yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((p) => {
                    const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
                    const displayAmount = isIndia
                      ? `₹${Number(p.amount_inr).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                      : convertBalance(Number(p.amount_inr));
                    return (
                      <div key={p.id} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: sc.bg }}>
                            <Icon name="BanknotesIcon" size={18} style={{ color: sc.color }} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{displayAmount}</p>
                            {!isIndia && (
                              <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>≈ {toUSDFromInr(Number(p.amount_inr))} USD</p>
                            )}
                            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{p.notes || 'Withdrawal request'}</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                              Requested: {new Date(p.requested_at).toLocaleDateString()}
                              {p.processed_at && ` · Processed: ${new Date(p.processed_at).toLocaleDateString()}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.color }}>
                          {sc.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Withdrawal Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="w-full max-w-md rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>New Withdrawal Request</h3>
                <button onClick={() => setShowModal(false)} className="btn-ghost p-1.5 rounded-md">
                  <Icon name="XMarkIcon" size={18} />
                </button>
              </div>

              {/* Show connected bank in modal */}
              {bankProfile?.verified && (
                <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <Icon name="CheckCircleIcon" size={16} style={{ color: '#10b981', flexShrink: 0 }} />
                  <div>
                    <p className="text-xs font-semibold" style={{ color: '#10b981' }}>Verified &amp; Active</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {bankProfile.accountHolderName} · {bankProfile.accountNumber}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleWithdraw} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Payout Method *</label>
                  <select
                    value={selectedPayoutMethod}
                    onChange={(e) => setSelectedPayoutMethod(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                    required
                  >
                    {payoutMethods.methods.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Amount ({currencyConfig.symbol}) *
                  </label>
                  <input
                    type="number"
                    min={isIndia ? '2500' : '10'}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder={isIndia ? 'Minimum ₹2,500' : `Minimum ${currencyConfig.symbol}10`}
                    className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
                    style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                    required
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                    Minimum: {isIndia ? '₹2,500' : `${currencyConfig.symbol}10`}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Notes (optional)</label>
                  <textarea
                    value={withdrawNotes}
                    onChange={(e) => setWithdrawNotes(e.target.value)}
                    rows={2}
                    placeholder="Reason for withdrawal..."
                    className="w-full px-4 py-2.5 rounded-xl text-sm outline-none resize-none"
                    style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                  />
                </div>
                {submitError && (
                  <p className="text-xs font-semibold" style={{ color: 'var(--danger)' }}>{submitError}</p>
                )}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold border" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--primary)' }}>
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
